import log from 'loglevel'
import {
  Datagrid,
  type FieldProps,
  type FilterPayload,
  Form,
  type Identifier,
  ListBase,
  Loading,
  Pagination,
  type RaRecord,
  type SortPayload,
  TextField,
  useGetList,
  useListContext,
  useNotify,
  useRecordContext,
  useTranslate,
  useUpdate
} from 'react-admin'
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { TextInput, useScroll } from 'components/Util'
import React from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import { includes, isEmpty, isEqual, isNil, keys, replace } from 'lodash/fp'
import {
  type Partner,
  type PartnerContractor,
  type PartnerUser
} from '@mzc-watchover/adminapp-appsync'
import { assign } from 'lodash'
import { doRetry, getPdf, postPdf } from 'components/DataProvider/functions'

type SearchParams = {
  name: string
  address: string
  place: string
}

type PartnerUserSub = Partial<PartnerUser> & {
  id: string
  contractor?: Partial<PartnerContractor>
}
const SearchButton: React.FC<{
  setFilter: (filter: FilterPayload) => void
}> = ({ setFilter }) => {
  const [enable, setEnable] = React.useState(false)
  const { getValues } = useFormContext()
  const { errors, isValid, dirtyFields } = useFormState()
  const translate = useTranslate()
  const onSubmit = (): void => {
    const { ...data } = getValues() as SearchParams
    const filter = {}
    for (const field of keys(dirtyFields)) {
      assign(filter, {
        [field]: data[field]
      })
    }
    setFilter(filter)
  }
  React.useEffect(() => {
    setEnable(isValid)
  }, [errors, isValid])
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      fullWidth
      disabled={!enable}
      onClick={onSubmit}>
      <Typography variant="button">
        {translate('resources.externalUsers.edit.buttons.search')}
      </Typography>
    </Button>
  )
}
const SubmitButton: React.FC<{
  record: PartnerUserSub
  setRecord: (data: PartnerUserSub) => void
}> = ({ record, setRecord }) => {
  const [enable, setEnable] = React.useState(false)
  const [data, setData] = React.useState({})
  const { getValues } = useFormContext()
  const translate = useTranslate()
  const { errors } = useFormState()
  const [update] = useUpdate(undefined, undefined, {
    onSettled: (data: PartnerUserSub | undefined) => {
      if (!isNil(data)) setRecord(data)
    }
  })
  const values = getValues()
  const onSubmit = (): void => {
    const { id } = record
    setEnable(false)
    update('users', {
      id,
      data,
      previousData: record
    }).catch(log.error)
  }
  const ignoreKeys = ['attributes', 'contractor', 'criterions', 'families']
  React.useEffect(() => {
    if (isEmpty(errors)) {
      const input = {}
      for (const key of keys(values)) {
        if (includes(key)(ignoreKeys)) continue
        if (!isNil(values[key]) && values[key] !== record[key]) {
          assign(input, { [key]: values[key] })
        }
      }
      const { contractor } = values
      const recordContractor = record.contractor ?? {
        address: undefined,
        name: undefined,
        nameKana: undefined,
        phone: undefined,
        postalCode: undefined
      }
      const contractorData = {}
      for (const key of keys(contractor)) {
        if (
          !isNil(contractor[key]) &&
          contractor[key] !== recordContractor[key]
        ) {
          assign(contractorData, { [key]: contractor[key] })
        }
      }
      if (!isEmpty(contractorData)) {
        assign(input, { contractor: contractorData })
      }
      if (!isEqual(input)(data)) {
        setEnable(!isEmpty(input))
        if (!isEmpty(input)) setData(input)
      }
    }
  }, [errors, values, record])
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      fullWidth
      disabled={!enable}
      onClick={onSubmit}>
      <Typography variant="button">
        {translate('resources.externalUsers.show.buttons.submit')}
      </Typography>
    </Button>
  )
}
const UserEditForm: React.FC<{
  setFilter: (filter: FilterPayload) => void
  partner: Partial<Partner> | undefined
}> = ({ setFilter, partner }) => {
  const translate = useTranslate()
  const validate = (values: any): Record<string, string> => {
    const { name, address, place } = values as SearchParams
    const errors: { children?: string } = {}
    if (isNil(name) && isNil(address) && isNil(place)) {
      errors.children = 'ra.validation.required'
    }
    return errors
  }
  if (isNil(partner)) return <></>
  return (
    <Form
      validate={validate}
      mode="onChange"
      reValidateMode="onChange"
      defaultValues={{
        name: null,
        address: null,
        place: null,
        attributes: null
      }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <Box sx={{ width: '1280px' }}>
          <Box
            sx={{
              marginLeft: '0.5em',
              marginRight: '0.5em'
            }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                marginTop: '0.1em'
              }}>
              <Grid item xs={12}>
                <Typography variant="h6">
                  {translate('resources.externalUsers.edit.title')}
                </Typography>
              </Grid>
              <Grid
                item
                xs={8}
                sx={{ marginTop: '0.25em', marginBottom: '0.25em' }}>
                <TextInput
                  source="name"
                  placeholder="中電太郎"
                  fields="resources.externalUsers.edit.fields"
                />
              </Grid>
              <Grid
                item
                xs={8}
                sx={{ marginTop: '0.25em', marginBottom: '0.25em' }}>
                <TextInput
                  source="address"
                  placeholder="愛知県名古屋市中区"
                  fields="resources.externalUsers.edit.fields"
                />
              </Grid>
              <Grid item xs={8} sx={{ marginTop: '0.5em' }}>
                <SearchButton setFilter={setFilter} />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Form>
  )
}

const AttributeField: React.FC<FieldProps> = () => {
  const record = useRecordContext()
  const { attributes } = record
  if (isNil(attributes) || isNil(attributes[0])) return <></>
  const { key, value } = attributes[0]
  return <TextField record={{ [key]: value }} source={key} />
}
const UserListContent: React.FC<{
  setRecord: (data: PartnerUserSub) => void
  scrollRef: React.RefObject<HTMLDivElement>
}> = ({ setRecord, scrollRef }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const { data, total, isLoading } = useListContext()
  React.useEffect(() => {
    if (isLoading) return
    if (total < 2) {
      setRecord(data[0])
      if (total === 0) {
        notify('resources.externalUsers.show.notifications.notFound')
      }
    }
  }, [isLoading, data, total])
  const select = (
    _id: Identifier,
    _resource: string,
    record: RaRecord
  ): string => {
    scrollRef?.current?.scrollIntoView()
    setRecord(record as PartnerUserSub)
    return 'toggleSelection'
  }
  const ref = useScroll(total)
  if (isLoading) return <Loading />
  if (total < 2 || isNil(data)) return <></>
  let attributeKey
  if (!isNil(data[0])) {
    const { attributes } = data[0]
    if (!isNil(attributes) && !isNil(attributes[0])) {
      attributeKey = attributes[0].key
    }
  }
  return (
    <Grid
      container
      justifyContent="center"
      spacing={2}
      sx={{ marginTop: '0.5em' }}>
      <Grid item xs={12}>
        <Typography variant="h6">
          {translate('resources.externalUsers.show.list.title')}
        </Typography>
        <Datagrid
          rowClick={select}
          bulkActionButtons={false}
          sx={{ marginTop: '0.5em' }}>
          {!isNil(attributeKey) && <AttributeField source={attributeKey} />}
          <TextField source="name" />
          <TextField source="address" />
        </Datagrid>
        {total > 10 && <Pagination />}
        <div ref={ref} />
      </Grid>
    </Grid>
  )
}
const UserList: React.FC<{
  filter: FilterPayload
  setRecord: (data: PartnerUserSub) => void
  scrollRef: React.RefObject<HTMLDivElement>
}> = ({ filter, setRecord, scrollRef }) => {
  const sort: SortPayload = { field: 'name', order: 'ASC' }
  return (
    <ListBase resource="users" sort={sort} filter={filter}>
      <UserListContent setRecord={setRecord} scrollRef={scrollRef} />
    </ListBase>
  )
}
const UserShowForm: React.FC<{
  filter: FilterPayload
  partner: Partial<Partner> | undefined
}> = ({ filter, partner }) => {
  const [record, setRecord] = React.useState<PartnerUserSub | null>(null)
  const schema = z.object({
    phone: z
      .string()
      .min(11, { message: 'too small' })
      .max(11, { message: 'too large' })
  })
  const ref = useScroll(record)
  if (isNil(partner)) return null
  return (
    <Form
      resolver={zodResolver(schema)}
      mode="onChange"
      reValidateMode="onChange"
      defaultValues={{
        ...record
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '1280px' }}>
          <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
            <UserList filter={filter} setRecord={setRecord} scrollRef={ref} />
          </Box>
          {!isNil(record) && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ marginTop: '1em' }}>
                  <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="name"
                          placeholder="中電太郎"
                          fields="resources.externalUsers.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="nameKana"
                          placeholder="チュウデンタロウ"
                          fields="resources.externalUsers.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="postalCode"
                          placeholder="0123456"
                          fields="resources.externalUsers.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="address"
                          placeholder="愛知県名古屋市中区"
                          fields="resources.externalUsers.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="phone"
                          placeholder="09012345678"
                          fields="resources.externalUsers.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="sms"
                          placeholder="09012345678"
                          fields="resources.externalUsers.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="spin"
                          placeholder="22桁の半角数字"
                          fields="resources.externalUsers.show.fields"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ marginTop: '1em' }}>
                  <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="contractor.name"
                          placeholder="中電花子"
                          fields="resources.externalUsers.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="contractor.nameKana"
                          placeholder="チュウデンハナコ"
                          fields="resources.externalUsers.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="contractor.postalCode"
                          placeholder="0123456"
                          fields="resources.externalUsers.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="contractor.address"
                          placeholder="愛知県名古屋市中区"
                          fields="resources.externalUsers.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="contractor.phone"
                          placeholder="09012345678"
                          fields="resources.externalUsers.show.fields"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
          {!isNil(record) && (
            <Box sx={{ marginTop: '1em' }}>
              <Grid
                container
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: '0.1em'
                }}
                spacing={5}>
                <Grid item xs={2}>
                  <SubmitButton record={record} setRecord={setRecord} />
                </Grid>
                <Grid item xs={2}>
                  <PdfUploadButton
                    partner={partner.id as string}
                    record={record}
                  />
                </Grid>
                <Grid item xs={2}>
                  <PdfDownladButton
                    partner={partner.id as string}
                    record={record}
                  />
                </Grid>
                <Grid item xs={2.2}>
                  <RetryButton partner={partner.id as string} record={record} />
                </Grid>
              </Grid>
              <div ref={ref} />
            </Box>
          )}
        </Box>
      </Box>
    </Form>
  )
}
const RetryButton: React.FC<{
  partner: string
  record: PartnerUserSub
}> = ({ partner, record }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const onClick = (): void => {
    const { id } = record
    notify('retrying')
    doRetry(partner, id)
      .then((status: number) => {
        if (status !== 202) {
          notify('failed retry')
        }
      })
      .catch(() => {
        notify('failed retry')
      })
  }
  return (
    <Button
      variant="outlined"
      onClick={onClick}
      sx={{ borderRadius: '10px' }}
      fullWidth
      size="large">
      {translate('resources.externalUsers.show.buttons.retry')}
    </Button>
  )
}
const PdfUploadButton: React.FC<{
  partner: string
  record: PartnerUserSub
}> = ({ partner, record }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const upload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (
      isNil(event.target.files) ||
      isNil(event.target.files[0]) ||
      isNil(record)
    )
      return
    notify('uploading pdf')
    const reader = new FileReader()
    reader.onload = () => {
      if (isNil(reader.result)) return
      const file = replace(
        /^data:application\/pdf;base64,/,
        ''
      )(reader.result as string)
      const { id } = record
      postPdf(partner, id, file)
        .then((status: number) => {
          if (status !== 202) {
            notify('cannot upload pdf')
          }
        })
        .catch(() => {
          notify('cannot upload pdf')
        })
    }
    reader.readAsDataURL(event.target.files[0])
    event.target.value = ''
  }
  const onClick = (): void => {
    if (!isNil(inputRef.current)) {
      inputRef.current.click()
    }
  }
  const inputRef = React.useRef<HTMLInputElement>(null)
  return (
    <>
      <Button
        variant="outlined"
        onClick={onClick}
        sx={{ borderRadius: '10px' }}
        fullWidth
        size="large">
        {translate('resources.externalUsers.show.buttons.pdfUpload')}
      </Button>
      <input
        hidden
        ref={inputRef}
        type="file"
        accept="application/pdf"
        onChange={upload}
      />
    </>
  )
}
const PdfDownladButton: React.FC<{
  partner: string
  record: PartnerUserSub
}> = ({ partner, record }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const onClick = (): void => {
    if (isNil(record)) return
    notify('downloading pdf')
    const { id } = record
    getPdf(partner, id)
      .then((item: { data: string | undefined; status: number }) => {
        const { data } = item
        if (isNil(data)) {
          notify('cannot download pdf')
        } else {
          const blob = new Blob([Buffer.from(data, 'base64')], {
            type: 'application/pdf'
          })
          const url = URL.createObjectURL(blob)
          window.open(url)
        }
      })
      .catch(() => {
        notify('cannot download pdf')
      })
  }
  return (
    <Button
      variant="outlined"
      onClick={onClick}
      sx={{ borderRadius: '10px' }}
      fullWidth
      size="large">
      {translate('resources.externalUsers.show.buttons.pdf')}
    </Button>
  )
}
export const ExternalUserEdit: React.FC<{
  partner: Partial<Partner> | undefined
  userId: string | null | undefined
}> = ({ partner, userId, ...props }) => {
  const [filter, setFilter] = React.useState<FilterPayload | undefined>(
    undefined
  )
  const [target, setTarget] = React.useState<PartnerUser | undefined>(undefined)
  const { data, isLoading } = useGetList<PartnerUser>('users', {
    pagination: { page: 1, perPage: 1 }
  })
  React.useEffect(() => {
    if (isLoading || !isNil(target)) return
    if (!isNil(data)) setTarget(data[0])
  }, [isLoading, data, target])
  React.useEffect(() => {
    if (isNil(userId)) return
    setFilter({ id: userId })
  }, [userId])
  if (isLoading) return <Loading />
  return (
    <>
      {isNil(userId) && (
        <Card raised sx={{ width: '1280px', mt: '1em', mb: '1em' }}>
          <CardContent>
            <UserEditForm {...props} setFilter={setFilter} partner={partner} />
          </CardContent>
        </Card>
      )}
      {!isNil(filter) && (
        <Card
          raised
          sx={{
            width: '1280px',
            mt: '1em',
            mb: '1em'
          }}>
          <CardContent>
            <UserShowForm {...props} filter={filter} partner={partner} />
          </CardContent>
        </Card>
      )}
    </>
  )
}
